import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { ExpandMore, Restore } from '@mui/icons-material';
import { formatDate } from '../Formatters';
import { RichText } from '../components/richText';
import { GetTermQuery } from '../__generated__/graphql';
import { RESTORE } from './dictionary.graphql';

type Version = NonNullable<NonNullable<NonNullable<GetTermQuery['term']>['versions']>[number]>;

const LABELS: Record<string, string> = {
  preferred: 'Begrep (vi velger)',
  synonyms: 'Alternativ (vi velger ikke)',
  categories: 'Fagområder',
  seeAlso: 'Se også',
  definition: 'Definisjon',
  notes: 'Merknad om bruk',
};
const FIELDS = Object.keys(LABELS);

const words = (v: Version | undefined, field: string): string[] => {
  if (!v) return [];
  if (field === 'categories') return (v.categories ?? []).map((c) => c?.title ?? '');
  if (field === 'seeAlso') return (v.seeAlso ?? []).map((t) => t?.preferred ?? '');
  return (v.synonyms ?? []).filter((x): x is string => !!x);
};
const text = (v: Version | undefined, field: string): string => (v ? ((v as unknown as Record<string, string | null>)[field] ?? '') : '');
const isList = (field: string) => ['synonyms', 'categories', 'seeAlso'].includes(field);
const isRich = (field: string) => field === 'definition' || field === 'notes';

/** Every saved version of a term, newest first, with what changed and a restore button for editors. */
export function TermHistory({
  termId,
  versions,
  canEdit,
  onUpdate,
}: {
  termId: string;
  versions: Version[];
  canEdit: boolean;
  onUpdate: () => Promise<unknown>;
}) {
  const [restore, { loading }] = useMutation(RESTORE);
  const [error, setError] = useState<string>();

  const restoreVersion = async (version: number) => {
    if (!window.confirm(`Gjenopprette versjon ${version}? Den lagres som en ny versjon.`)) return;
    setError(undefined);
    const result = (await restore({ variables: { input: { id: termId, version } } })).data?.termRestore;
    const message = result?.errors?.map((e) => ('message' in e ? e.message : '')).join(' ');
    if (message) setError(message);
    else await onUpdate();
  };

  return (
    <div>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {versions.map((v, i) => (
        <VersionItem
          key={v.version}
          version={v}
          previous={versions[i + 1]}
          isCurrent={i === 0}
          onRestore={canEdit && i > 0 && !loading ? () => restoreVersion(v.version) : undefined}
        />
      ))}
    </div>
  );
}

function VersionItem({
  version,
  previous,
  isCurrent,
  onRestore,
}: {
  version: Version;
  previous?: Version;
  isCurrent: boolean;
  onRestore?: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const changed = version.changedFields?.filter((f): f is string => !!f) ?? [];
  const summary = version.restoredFromVersion
    ? `Gjenopprettet fra versjon ${version.restoredFromVersion}`
    : previous
      ? `Endret: ${changed.map((f) => LABELS[f] ?? f).join(', ') || 'ingenting'}`
      : 'Opprettet';

  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack>
          <Typography variant="subtitle2">
            Versjon {version.version}
            {isCurrent && ' (gjeldende)'} · {version.createdBy?.name ?? 'Ukjent'} · {formatDate(version.createdAt)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {summary}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {(showAll ? FIELDS : changed).map((field) => (
            <FieldChange key={field} field={field} version={version} previous={previous} showChange={changed.includes(field)} />
          ))}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label="Vis hele versjonen" />
            {onRestore && (
              <Button startIcon={<Restore />} onClick={onRestore}>
                Gjenopprett denne versjonen
              </Button>
            )}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function FieldChange({ field, version, previous, showChange }: { field: string; version: Version; previous?: Version; showChange: boolean }) {
  const diff = showChange && !!previous;
  let content: React.ReactNode;

  if (isList(field)) {
    const now = words(version, field);
    const before = words(previous, field);
    const removed = diff ? before.filter((w) => !now.includes(w)) : [];
    content = (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {now.map((w) => (
          <Chip key={w} size="small" label={w} color={diff && !before.includes(w) ? 'success' : 'default'} />
        ))}
        {removed.map((w) => (
          <Chip key={`-${w}`} size="small" label={w} color="error" variant="outlined" sx={{ textDecoration: 'line-through' }} />
        ))}
        {now.length === 0 && removed.length === 0 && <Typography color="textSecondary">Ingen</Typography>}
      </Stack>
    );
  } else {
    const render = (value: string) =>
      !value ? (
        <Typography color="textSecondary">Ingen</Typography>
      ) : isRich(field) ? (
        <RichText html={value} component="div" />
      ) : (
        <Typography>{value}</Typography>
      );
    content = diff ? (
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <Box sx={{ opacity: 0.6 }}>
          <Typography variant="caption">Før</Typography>
          {render(text(previous, field))}
        </Box>
        <Box>
          <Typography variant="caption">Etter</Typography>
          {render(text(version, field))}
        </Box>
      </Box>
    ) : (
      render(text(version, field))
    );
  }

  return (
    <div>
      <Typography variant="subtitle2">{LABELS[field] ?? field}</Typography>
      {content}
    </div>
  );
}
